import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import { COMPANIES } from "../constants/menu-types";
import { useLocale } from '../contexts/localeContext';
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from '../contexts/TranslationKeys';
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/CompanyColumns';
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from '../models/search/CompanySearch';
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { deleteCompany, getCompanies } from "../services/companyService";
import { getSiteNamesWithPagination } from "../services/siteService";
import {Filter} from "../models/Filter";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange: (filters: Filter[]) => void,
}

const CompaniesTab: FC<Props> = ({ setBusy, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const itemPath = '/access-control/enrolment/company/';
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const title = translate.byKey(TranslationKeys.companies);

    useEffect(() => {
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, [])

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteCompany(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    // --> start context
    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = COMPANIES;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
        setDetails(userLoginId, currentMenuDetail);
    }
    // <-- end context

    return <TableContextProvider
        dataMethod={getCompanies}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        deleteMethod={onDelete}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useFilter
        useSearch
        useSort
        lookup={lookup}
        customDelete={{ fieldName: "isDefault", comparisonValue: false }} >
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath}
            cyName="companies"/>
    </TableContextProvider>;
};

export default CompaniesTab;
